
import { z } from "zod";
import { SOCIAL_PROVIDERS } from "@artha/types";
import { normalizeSocialUrl, validateSocialProfileUrl } from "@artha/utils";

export const CreateUserSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  password: z.string()
});

export type CreateUserDto = z.infer<typeof CreateUserSchema>;

export const SocialProviderSchema = z.enum(SOCIAL_PROVIDERS);

export const SocialAuthRedirectParamsSchema = z.object({
  provider: SocialProviderSchema,
});

export const SocialAuthRedirectQuerySchema = z.object({
  username: z.string().optional(),
  referralToken: z.string().optional(),
});

export const SocialAuthCallbackQuerySchema = z.object({
  code: z.string(),
  state: z.string(),
});

export const SocialAuthLinkBodySchema = z.object({
  provider: SocialProviderSchema,
  providerUserId: z.string(),
});

export const SocialLinkProfileSchema = z.object({
  provider: SocialProviderSchema,
  profileUrl: z.string().min(1, "Profile URL is required"),
  timezone: z.string().optional(),
}).refine(
  (data) => {
    const normalizedUrl = normalizeSocialUrl(data.provider, data.profileUrl);
    const error = validateSocialProfileUrl(data.provider, normalizedUrl);
    return error === null;
  },
  {
    message: "Invalid profile URL for selected platform",
    path: ["profileUrl"],
  }
);

export type SocialProviderDto = z.infer<typeof SocialProviderSchema>;
export type SocialAuthRedirectParamsDto = z.infer<
  typeof SocialAuthRedirectParamsSchema
>;
export type SocialAuthRedirectQueryDto = z.infer<
  typeof SocialAuthRedirectQuerySchema
>;
export type SocialAuthCallbackQueryDto = z.infer<
  typeof SocialAuthCallbackQuerySchema
>;
export type SocialAuthLinkBodyDto = z.infer<typeof SocialAuthLinkBodySchema>;
export type SocialLinkProfileDto = z.infer<typeof SocialLinkProfileSchema>;

export const OnboardingStatusResponseSchema = z.object({
  isApproved: z.boolean(),
  isComplete: z.boolean(),
});

export type OnboardingStatusResponseDto = z.infer<
  typeof OnboardingStatusResponseSchema
>;

export * from "./enrichment";
export * from "./creator";
export * from "./jobs";
export * from "./click-tracking";
export * from "./email";
export * from "./revenue-hub";
export * from "./insights";
export * from "./geolocation";
export * from "./referrals";
export * from "./developer";
